import { create } from 'zustand'
import { convex } from '../lib/convex'
import { api } from '../../convex/_generated/api'
import type { Id } from '../../convex/_generated/dataModel'
import { useAuthStore } from './authStore'
import { logger } from '../utils/logger'
import { maybeEncryptForFamily } from '../utils/writeEncryption'

/**
 * Compatibility types matching old Supabase shape.
 * Convex data is mapped to these types in FamilyDataSync component.
 */
export interface Family {
  id: string
  name: string
  home_a_name: string
  home_b_name: string
  created_at: string
  /**
   * Timestamp (ms) kiedy E2EE zostało aktywowane dla tej rodziny.
   * null / undefined = bypass (zapisy idą plaintextem dopóki wszyscy nie mają kluczy).
   */
  encryption_active_since: number | null
}

export interface FamilyMember {
  id: string
  user_id: string
  family_id: string
  role: string
  joined_at: string | null
  invited_at: string | null
  user_email?: string
}

export interface Child {
  id: string
  family_id: string
  name: string
  birth_date: string | null
  created_at: string
}

interface FamilyState {
  family: Family | null
  members: FamilyMember[]
  children: Child[]
  isLoading: boolean
  error: string | null
  initialized: boolean

  // Data setters (called from FamilyDataSync component)
  setFamily: (family: Family | null) => void
  setMembers: (members: FamilyMember[]) => void
  setChildren: (children: Child[]) => void
  setIsLoading: (isLoading: boolean) => void
  setError: (error: string | null) => void
  setInitialized: (initialized: boolean) => void

  // Actions — call Convex mutations via ConvexReactClient
  initialize: () => Promise<void>
  createFamily: (name: string) => Promise<string | null>
  updateFamily: (data: { name?: string; home_a_name?: string; home_b_name?: string }) => Promise<boolean>
  addChild: (name: string) => Promise<string | null>
  updateChild: (childId: string, data: { name?: string }) => Promise<boolean>
  removeChild: (childId: string) => Promise<boolean>
  inviteFamilyMember: (email: string, role: 'parent_a' | 'parent_b' | 'caregiver' | 'child_viewer') => Promise<string | null>
  fetchFamily: (familyId: string) => Promise<void>
  fetchChildren: (familyId: string) => Promise<void>
  fetchFamilyMembers: (familyId: string) => Promise<void>
  getUserFamilyId: () => Promise<string | null>
  hasFamily: () => boolean
  clearCache: () => void
}

export const useFamilyStore = create<FamilyState>()(
    (set, get) => ({
      family: null,
      members: [],
      children: [],
      isLoading: false,
      error: null,
      initialized: false,

      // Data setters for FamilyDataSync
      setFamily: (family) => set({ family }),
      setMembers: (members) => set({ members }),
      setChildren: (children) => set({ children }),
      setIsLoading: (isLoading) => set({ isLoading }),
      setError: (error) => set({ error }),
      setInitialized: (initialized) => set({ initialized }),

      // No-op: data comes from Convex reactive queries via FamilyDataSync
      initialize: async () => {
        // FamilyDataSync handles initialization via reactive queries.
        // This is kept for backward compatibility with App.tsx calls.
        logger.debug('FamilyStore: initialize() called (no-op, data from Convex reactive queries)')
      },

      createFamily: async (name: string) => {
        const { user } = useAuthStore.getState()
        if (!user) {
          set({ error: 'You must be logged in to create a family', isLoading: false })
          return null
        }

        logger.debug('Creating new family:', name)
        set({ isLoading: true, error: null })

        try {
          const familyId = await convex.mutation(
            api.families.mutations.createFamily,
            { name: name.trim() }
          )

          logger.debug('Family created:', familyId)
          set({ isLoading: false })
          return familyId as string
        } catch (error: any) {
          console.error('Error creating family:', error)
          set({
            error: error.message || 'Error creating family',
            isLoading: false,
          })
          return null
        }
      },

      updateFamily: async (data) => {
        const { family } = get()
        if (!family) return false

        logger.debug('Updating family data...')
        set({ isLoading: true, error: null })

        try {
          await convex.mutation(api.families.mutations.updateFamily, {
            familyId: family.id as Id<"families">,
            name: data.name,
            homeAName: data.home_a_name,
            homeBName: data.home_b_name,
          })

          logger.debug('Family data updated')
          // Convex reactive query will auto-update the store via FamilyDataSync
          set({ isLoading: false })
          return true
        } catch (error: any) {
          console.error('Error updating family data:', error)
          set({
            error: error.message || 'Error updating family data',
            isLoading: false,
          })
          return false
        }
      },

      addChild: async (name) => {
        const { family } = get()
        if (!family) return null

        logger.debug(`Adding child to family ${family.id}...`)
        set({ isLoading: true, error: null })

        try {
          const childName = await maybeEncryptForFamily(name.trim(), family)

          const childId = await convex.mutation(
            api.families.mutations.addChild,
            {
              familyId: family.id as Id<"families">,
              name: childName,
            }
          )

          logger.debug('Child added:', childId)
          // Convex reactive query will auto-update children list
          set({ isLoading: false })
          return childId as string
        } catch (error: any) {
          console.error('Error adding child:', error)
          set({
            error: error.message || 'Error adding child',
            isLoading: false,
          })
          return null
        }
      },

      updateChild: async (childId, data) => {
        const { family } = get()
        if (!family) return false

        logger.debug(`Updating child ${childId}...`)
        set({ isLoading: true, error: null })

        try {
          const trimmed = data.name?.trim()
          const nameToSave = trimmed
            ? await maybeEncryptForFamily(trimmed, family)
            : trimmed

          await convex.mutation(api.families.mutations.updateChild, {
            childId: childId as Id<"children">,
            familyId: family.id as Id<"families">,
            name: nameToSave,
          })

          logger.debug('Child updated:', childId)
          set({ isLoading: false })
          return true
        } catch (error: any) {
          console.error('Error updating child:', error)
          set({
            error: error.message || 'Error updating child',
            isLoading: false,
          })
          return false
        }
      },

      removeChild: async (childId) => {
        const { family } = get()
        if (!family) return false

        logger.debug(`Removing child ${childId}...`)
        set({ isLoading: true, error: null })

        try {
          await convex.mutation(api.families.mutations.removeChild, {
            childId: childId as Id<"children">,
            familyId: family.id as Id<"families">,
          })

          logger.debug('Child removed:', childId)
          // Calendar entries cascade-deleted by Convex mutation
          set({ isLoading: false })
          return true
        } catch (error: any) {
          console.error('Error removing child:', error)
          set({
            error: error.message || 'Error removing child',
            isLoading: false,
          })
          return false
        }
      },

      inviteFamilyMember: async (email, role) => {
        const { family } = get()
        if (!family) return null

        logger.debug(`Inviting ${email} to family ${family.id}...`)
        set({ isLoading: true, error: null })

        try {
          const invitationId = await convex.mutation(
            api.invitations.mutations.createInvitation,
            {
              familyId: family.id as Id<"families">,
              email: email.toLowerCase(),
              role: role as "parent_a" | "parent_b" | "child_viewer",
            }
          )

          logger.debug('Invitation created:', invitationId)
          set({ isLoading: false })
          return invitationId as unknown as string
        } catch (error: any) {
          console.error('Error inviting family member:', error)
          set({
            error: error.message || 'Error inviting family member',
            isLoading: false,
          })
          return null
        }
      },

      // No-op: data comes from Convex reactive queries
      fetchFamily: async (_familyId) => {
        logger.debug('fetchFamily() called (no-op, data from Convex reactive queries)')
      },

      fetchChildren: async (_familyId) => {
        logger.debug('fetchChildren() called (no-op, data from Convex reactive queries)')
      },

      fetchFamilyMembers: async (_familyId) => {
        logger.debug('fetchFamilyMembers() called (no-op, data from Convex reactive queries)')
      },

      getUserFamilyId: async () => {
        const { family } = get()
        return family?.id || null
      },

      hasFamily: () => {
        return get().family !== null
      },

      clearCache: () => {
        logger.debug('Clearing family data cache')
        // No sessionStorage cache to clear with Convex
        // Just reset local state
        set({
          family: null,
          members: [],
          children: [],
          initialized: false,
        })
      },
    })
)
